import React from 'react';
import A2PBypassBanner from '../../../components/A2PBypassBanner.jsx';
import AgentBillingSetup from '../../../components/onboarding/AgentBillingSetup.jsx';
import {
  X,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import StepBar from './wizard/StepBar.jsx';
import StepType from './wizard/StepType.jsx';
import StepSearch from './wizard/StepSearch.jsx';
import StepSelect from './wizard/StepSelect.jsx';
import StepPurchase from './wizard/StepPurchase.jsx';
import StepBulkSummary from './wizard/StepBulkSummary.jsx';
import StepProvisioning from './wizard/StepProvisioning.jsx';
import ActiveNumbersTable from './ActiveNumbersTable.jsx';
import ConfirmDialog from './dialogs/ConfirmDialog.jsx';
import useNumbersWizard from '../hooks/useNumbersWizard.js';
import ProducerAgreementStep from '../../../components/onboarding/ProducerAgreementStep.jsx';

/**
 * Multi-step number purchase wizard with the agent's active lines listed underneath.
 * @returns {JSX.Element}
 */
export default function NumbersWizard() {
  const {
    nav,
    agent,
    isParentBilled,
    needsWallet,
    step,
    setStep,
    numberType,
    setNumberType,
    searchMode,
    setSearchMode,
    areaCode,
    setAreaCode,
    states,
    setStates,
    results,
    simulated,
    selectedNumbers,
    setSelectedNumbers,
    searching,
    purchasing,
    purchased,
    bulkResult,
    fieldErrors,
    confirmCancel,
    setConfirmCancel,
    confirmTollFree,
    setConfirmTollFree,
    owned,
    refetchOwned,
    agreementSigned,
    agreementData,
    refetchAgreement,
    reset,
    selectedRecords,
    runSearch,
    runPurchase,
    goSettings,
    requestCancel,
  } = useNumbersWizard();

  if (needsWallet) {
    return (
      <div className="space-y-4">
        <div className="panel p-5 border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">
              $
            </div>
            <div>
              <div className="font-display font-bold text-white text-base">Self-Funded Telecom Wallet Setup</div>
              <p className="text-xs text-white/70">
                Platform access is 100% free for your 30-day trial. To lease phone numbers ($1.00/mo) and make calls, please add a card and make your initial wallet deposit.
              </p>
            </div>
          </div>
        </div>
        <AgentBillingSetup
          onComplete={() => {
            // AgentBillingSetup already wrote the new walletBalance into the auth
            // store, so `needsWallet` is false on the next render and the number
            // search (Step 1) appears with no page refresh.
            toast.success('Wallet funded! Ready to choose your phone number.');
            refetchOwned();
          }}
        />
      </div>
    );
  }

  return (
    <>
      {isParentBilled && <A2PBypassBanner variant="purchase" />}
      {!agreementSigned && (
        <div className="space-y-3">
          <div className="panel p-4 border border-amber-500/40 bg-amber-500/10 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-white font-medium">Sign your Producer Agreement to unlock phone setup</div>
              <p className="text-xs text-white/60 mt-1">
                Review the agreement below, check the box, type your full legal name and sign. This authorizes you
                to send SMS and place calls under CIVIX LLC&apos;s registered A2P Brand.
              </p>
            </div>
          </div>
          <ProducerAgreementStep
            data={agreementData}
            onSigned={() => { toast.success('Agreement signed — you can set up your phone now.'); refetchAgreement(); }}
          />
        </div>
      )}
      <div className="panel p-5 space-y-5">
        <StepBar current={step} />

        {step === 1 && <StepType numberType={numberType} setNumberType={setNumberType} onNext={() => setStep(2)} onSelectTollFree={() => setConfirmTollFree(true)} />}
        {step === 2 && (
          <StepSearch
            numberType={numberType}
            searchMode={searchMode} setSearchMode={setSearchMode}
            areaCode={areaCode} setAreaCode={setAreaCode}
            states={states} setStates={setStates}
            fieldErrors={fieldErrors} searching={searching}
            onBack={() => setStep(1)} onSearch={runSearch}
          />
        )}
        {step === 3 && (
          <StepSelect
            results={results}
            simulated={simulated}
            selectedNumbers={selectedNumbers}
            setSelectedNumbers={setSelectedNumbers}
            onBack={() => setStep(2)}
            onNext={() => selectedNumbers.size > 0 && setStep(4)}
          />
        )}
        {step === 4 && (
          <StepPurchase
            selectedRecords={selectedRecords}
            purchasing={purchasing} fieldErrors={fieldErrors}
            agent={agent}
            onBack={() => setStep(3)} onPurchase={runPurchase}
          />
        )}
        {step === 5 && purchased && (
          <StepProvisioning purchased={purchased} onGoSettings={goSettings} onGoTemplates={goSettings} onBuyAnother={reset} onSettled={refetchOwned} />
        )}
        {step === 5 && bulkResult && (
          <StepBulkSummary bulkResult={bulkResult} onGoSettings={goSettings} onBuyAnother={reset} />
        )}

        {step < 5 && (
          <div className="flex justify-end pt-2 border-t border-civix-border/40">
            <button
              type="button"
              onClick={requestCancel}
              className="text-xs text-rose-300 hover:text-rose-200 inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Cancel & start over
            </button>
          </div>
        )}
      </div>

      <ActiveNumbersTable owned={owned} />

      {confirmCancel && (
        <ConfirmDialog
          title="Discard this purchase?"
          body="Your selected number won't be reserved. You can start over anytime."
          confirmLabel="Discard"
          onCancel={() => setConfirmCancel(false)}
          onConfirm={reset}
        />
      )}

      {confirmTollFree && (
        <ConfirmDialog
          title="Switch to the Toll-Free flow?"
          body="Toll-Free numbers are voice-only in CIVIX and cannot be used for SMS outreach. Continue?"
          confirmLabel="Continue to Toll-Free"
          onCancel={() => { setConfirmTollFree(false); setNumberType('local'); }}
          onConfirm={() => { setConfirmTollFree(false); nav('/phone-setup/toll-free'); }}
        />
      )}
    </>
  );
}
